/* istanbul ignore file */
/* eslint-disable react/forbid-prop-types,max-len */
import React from 'react';
import PropTypes from 'prop-types';
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import ChannelService from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelService';
import {
  adjustFromDisplay,
  findInternalTempChannelGroupFromSensors,
  findSensorWithInternalTempChannelGroup,
  normalizeRaaChannelKey,
  parseChannelKey,
  trimRaaChannelKey,
} from '../../../../../utility/channelUtils';
import SensorsHeader from '../SensorsHeader';
import DeviceSensorCard from '../sensors/cards/DeviceSensorCard/DeviceSensorCard';
import SingleReadingCard from '../sensors/cards/SingleReadingCard';
import { getSecondsElapsed } from '../../../../../utility/displayValueUtils';
import BasicSensorSettingsModal from '../../modals/settings/BasicSensorSettingsModal/BasicSensorSettingsModal';
import {
  fetchSingleDevice,
  selectHiddenChannelGroups,
  toggleChannelGroupVisibility,
  updateSensor,
} from '../../../../../redux/slices/singleDeviceSlice/singleDeviceSlice';
import HiddenSensorsModal from '../../modals/HiddenSensorsModal';
import { ShowHiddenSensorsButton } from '../../modals/ShowHiddenSensorsButton';
import formatTimestamp from '../../../../../utility/formatTimestamp';
import { selectSessionInformation } from '../../../../../redux/slices/sessionSlice';

const DeviceSensors = ({
  sensors,
  deviceInfo,
  temperatureScale,
  isReadOnly = false,
}) => {
  const dispatch = useDispatch();
  const hiddenChannelGroups = useSelector(selectHiddenChannelGroups);
  const { timezone, activeSubscriptionId } = useSelector(selectSessionInformation);
  const preferencesDisplay = { temperatureScale };
  const [showSettingsModal, setShowSettingsModal] = React.useState(false);
  const [showHiddenSensorsModal, setShowHiddenSensorsModal] = React.useState(false);
  const internalSensor = findSensorWithInternalTempChannelGroup(sensors);
  const internalChannelGroup = findInternalTempChannelGroupFromSensors(sensors);
  const internalChannelKey = internalChannelGroup && internalChannelGroup.channels
        && internalChannelGroup.channels[0]
    ? internalChannelGroup.channels[0].channelKey
    : undefined;
  const internalChannelParsed = internalChannelKey ? parseChannelKey(internalChannelKey) : null;
  const internalChannel = internalChannelParsed
    ? ChannelService.getChannelGroupChannel(
      internalChannelParsed.channelGroupType,
      internalChannelParsed.channelIndex,
      internalChannelParsed.channelType,
    )
    : null;
  const channelGroupTypesToSkip = [1300];
  const isCurrentlyHidden = (channelKey) => hiddenChannelGroups.includes(channelKey);
  const isInternalSensorHidden = internalChannelGroup
    ? isCurrentlyHidden(internalChannelGroup.channelKey)
    : false;
  const assignmentStartDateTimestamp = deviceInfo && deviceInfo.deviceAssignmentStartDate
  && deviceInfo.deviceAssignmentStartDate.epochSecond
    ? deviceInfo.deviceAssignmentStartDate.epochSecond
    : 0;

  const handleSaveSettings = async (data) => {
    if (isReadOnly) return false;
    try {
      if (!internalChannelGroup || !internalChannelGroup.channels) {
        // eslint-disable-next-line no-console
        console.log('Channel group data is missing');
        return false;
      }
      const updatedChannels = internalChannelGroup.channels.map((channel) => {
        if (!channel || !channel.channelKey) {
          throw new Error('Invalid channel data');
        }
        const parsedKey = parseChannelKey(channel.channelKey);
        const channelInstance = ChannelService.getChannelGroupChannel(
          parsedKey.channelGroupType,
          parsedKey.channelIndex,
          parsedKey.channelType,
        );
        let adjust = 0;
        if (data.channelData[channel.channelKey]
            && data.channelData[channel.channelKey].adjust !== undefined) {
          adjust = parseInt(data.channelData[channel.channelKey].adjust, 10);
        }
        const normalizedAdjust = adjustFromDisplay(
          channelInstance,
          adjust,
          preferencesDisplay.temperatureScale,
        );
        return {
        // trimming sub id off channel key to match what backend is expecting
          channelKey: trimRaaChannelKey(channel.channelKey),
          adjust: normalizedAdjust,
        };
      });
      const payload = {
        channelKey: internalChannelGroup.channelKey,
        channels: updatedChannels,
        label: data.label,
        isHidden: data.isHidden,
      };
      await dispatch(updateSensor(payload)).unwrap();
      await dispatch(fetchSingleDevice({ deviceId: deviceInfo.id })).unwrap();
      return true;
    } catch (error) {
      throw new Error(error.message || 'Failed to save sensor settings');
    }
  };

  const toggleChannelGroupHidden = async (channelKey, label) => {
    if (isReadOnly) return;
    try {
      // passing through label because needed for api
      await dispatch(toggleChannelGroupVisibility({
        channelKey,
        label,
        isHidden: !isCurrentlyHidden(channelKey),
      })).unwrap();
      await dispatch(fetchSingleDevice()).unwrap();
      // eslint-disable-next-line no-empty
    } catch (error) {}
  };

  // eslint-disable-next-line implicit-arrow-linebreak
  return (
    <>
      <SensorsHeader
        handleSettingsClick={() => {}}
        startDate={assignmentStartDateTimestamp}
      />
      {!isReadOnly && (
      <ShowHiddenSensorsButton hiddenChannelGroups={hiddenChannelGroups} />
      )}
      <Row className="mx-auto card-container">
        {internalChannelGroup && internalChannel && !isInternalSensorHidden && (
        <Col key="hub" xs="auto" className="mb-2 px-1">
          <SingleReadingCard
            sensorName={internalChannelGroup.label}
            macAddress={internalChannelParsed.sensorMacAddress}
            typeName="Internal Temperature"
            lastUpdated={formatTimestamp(internalChannelGroup.lastReceived && internalChannelGroup.lastReceived.epochSecond, timezone, 'YYYY-MM-DD HH:mm:ss')}
            isHub={false}
            isWireless={false}
            reading1={{
              title: 'Temperature',
              value: internalChannel.getDisplayValue(internalChannelGroup.channels[0].value, {
                scale: preferencesDisplay.temperatureScale,
              }),
              units: internalChannel.getUnits({ scale: preferencesDisplay.temperatureScale }),
              isTriggered: null,
              channelKey: normalizeRaaChannelKey(internalChannelKey, activeSubscriptionId),
            }}
            rssi={0}
            onSettingsClick={() => (isReadOnly ? () => {} : setShowSettingsModal(true))}
            isMuted={getSecondsElapsed(internalChannelGroup.lastReceived && internalChannelGroup.lastReceived.epochSecond) > 7200}
          />
        </Col>
        )}

        {sensors && sensors.length > 0 && sensors.map((sensor) => (
          sensor && sensor.channelGroups && sensor.channelGroups
            .filter((group) => {
              if (!group || !group.channelKey) return false;
              const parsedKey = parseChannelKey(group.channelKey);
              return parsedKey
                      && !channelGroupTypesToSkip.includes(parsedKey.channelGroupType)
                      && group.channelKey !== (internalChannelGroup && internalChannelGroup.channelKey)
                      && !isCurrentlyHidden(group.channelKey);
            })
            .sort((a, b) => {
              const aMuted = getSecondsElapsed((a && a.lastReceived && a.lastReceived.epochSecond) || 0) > 7200;
              const bMuted = getSecondsElapsed((b && b.lastReceived && b.lastReceived.epochSecond) || 0) > 7200;
              // eslint-disable-next-line no-nested-ternary
              return aMuted === bMuted ? 0 : aMuted ? 1 : -1;
            })
            .map((group, index) => (
              <Col key={`${sensor.channelKey}-${index}`} xs="auto" className="mb-2 px-1">
                <DeviceSensorCard
                  channelGroup={group}
                  lastReceived={(group && group.lastReceived && group.lastReceived.epochSecond) || ''}
                  isHidden={isCurrentlyHidden(group.channelKey)}
                  onSettingsClick={isReadOnly ? () => {} : () => setShowSettingsModal(true)}
                  onToggleHidden={isReadOnly ? () => {} : () => toggleChannelGroupHidden(group.channelKey, group.label)}
                  isMuted={getSecondsElapsed((group && group.lastReceived && group.lastReceived.epochSecond) || 0) > 7200}
                  isReadOnly={isReadOnly}
                />
              </Col>
            ))
        ))}

      </Row>

      {showSettingsModal && internalSensor && internalChannelGroup && (
      <BasicSensorSettingsModal
        show={showSettingsModal}
        handleClose={() => setShowSettingsModal(false)}
        sensorLabel={internalChannelGroup.label}
        channelGroup={internalChannelGroup}
        onSave={handleSaveSettings}
        isHidden={isInternalSensorHidden}
        onToggleHidden={() => toggleChannelGroupHidden(
          internalChannelGroup.channelKey,
          internalChannelGroup.label,
        )}
      />
      )}

      {
        showHiddenSensorsModal && !showSettingsModal && (
        <HiddenSensorsModal
          handleClose={() => setShowHiddenSensorsModal(false)}
          show={showHiddenSensorsModal}
          hiddenChannelGroups={hiddenChannelGroups}
        />
        )
      }
    </>
  );
};

DeviceSensors.defaultProps = {
  isReadOnly: false,
  temperatureScale: 'C',
};

DeviceSensors.propTypes = {
  sensors: PropTypes.arrayOf(PropTypes.object).isRequired,
  deviceInfo: PropTypes.shape({
    id: PropTypes.number,
    uptime: PropTypes.string,
    pushStatus: PropTypes.number,
    isLinkConnected: PropTypes.bool,
    linkName: PropTypes.string,
    linkId: PropTypes.number,
    ethIp: PropTypes.string,
    wlanIp: PropTypes.string,
    isHttps: PropTypes.bool,
    httpPort: PropTypes.number,
    macAddress: PropTypes.string,
    serialnumber: PropTypes.string,
    firmware: PropTypes.string,
    type: PropTypes.string,
    warrantyEndDate: PropTypes.string,
    lastMessageTimestamp: PropTypes.shape({ epochSecond: PropTypes.number }),
    deviceAssignmentStartDate: PropTypes.shape({ epochSecond: PropTypes.number }),
  }).isRequired,
  isReadOnly: PropTypes.bool,
  temperatureScale: PropTypes.string,
};
export default DeviceSensors;
